import { Alert, Button, Col, Empty, Input, Popconfirm, Row, Select, Spin, Table, Tag, Typography } from "antd";
import {
  BranchesOutlined,
  CarOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PlusCircleOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Prompt } from "react-router-dom";
import AppModal from "../../components/AppModal/AppModal";
import { useNearbyRoutesUnsavedWarning } from "./NearbyRoutesDraftGuard";
import NearbyRouteService from "../../services/nearbyRoute.service";
import {
  isNearbyRouteFeatureDisabledError,
  refreshFeatureFlagsAfterNearbyRouteDisabled,
} from "../../store/actions/featureFlag.actions";
import { toastMessage } from "../../utils/utils";
import {
  appendSelectedRoute,
  buildOptionLabelLookup,
  buildAssociations,
  getMarketAssignmentLabels,
  getSubmarketAssignmentLabels,
  getTextDirection,
  haveSameRouteOrder,
  moveSelectedRoute,
  NEARBY_ROUTE_DEFAULTS,
  NEARBY_ROUTE_PAGE_SIZES,
  updateVisibleRouteSelections,
} from "./nearbyRoutes.util";
import "./NearbyRoutes.scss";

const EMPTY_PAGINATION = { totalItems: 0 };
const MAX_VISIBLE_SUBMARKETS = 3;
const EMPTY_VALUE = "—";
const ROUTE_TYPE_ICONS = Object.freeze({
  Airport: RocketOutlined,
  Highway: CarOutlined,
  Interstate: CarOutlined,
  Railroad: BranchesOutlined,
  Road: CarOutlined,
  Transit: BranchesOutlined,
});

const getErrorMessage = (error, fallback, correlationLabel) => {
  const message = error?.message || error || fallback;
  const correlationId = error?.response?.data?.correlationId || error?.correlationId;
  return correlationId ? `${message} ${correlationLabel}: ${correlationId}` : message;
};
const getColumnSortOrder = (column, activeColumn, direction) => {
  if (column !== activeColumn) return null;
  return direction === "asc" ? "ascend" : "descend";
};

const NearbyRoutes = ({
  draftRef,
  isActive,
  isPropertyReady,
  languageCode,
  marketId,
  markets,
  onSaveAndDisplay,
  propertyId,
  propertySubMarketId,
  propertyType,
  subMarkets,
  viewOnlyMode,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const draft = draftRef?.current;
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [subMarket, setSubMarket] = useState();
  const [routeType, setRouteType] = useState();
  const [page, setPage] = useState(NEARBY_ROUTE_DEFAULTS.PAGE);
  const [pageSize, setPageSize] = useState(NEARBY_ROUTE_DEFAULTS.PAGE_SIZE);
  const [sortField, setSortField] = useState(NEARBY_ROUTE_DEFAULTS.SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(NEARBY_ROUTE_DEFAULTS.SORT_DIRECTION);
  const [routes, setRoutes] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [routeTypes, setRouteTypes] = useState([]);
  const [availableSubMarketIds, setAvailableSubMarketIds] = useState([]);
  const [maximumSelections, setMaximumSelections] = useState(NEARBY_ROUTE_DEFAULTS.MAX_SELECTIONS);
  const [routeDetails, setRouteDetails] = useState(draft?.routeDetails ?? {});
  const [savedRouteIds, setSavedRouteIds] = useState(draft?.savedRouteIds ?? []);
  const [selectedRouteIds, setSelectedRouteIds] = useState(draft?.selectedRouteIds ?? []);
  const [version, setVersion] = useState(draft?.version ?? null);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [saving, setSaving] = useState(false);
  const [associationsError, setAssociationsError] = useState();
  const [resultsError, setResultsError] = useState();
  const [saveError, setSaveError] = useState();
  const [hasConflict, setHasConflict] = useState(draft?.hasConflict ?? false);
  const [conflictRecovered, setConflictRecovered] = useState(draft?.conflictRecovered ?? false);
  const [propertyNotFound, setPropertyNotFound] = useState(false);
  const [propertyMissing, setPropertyMissing] = useState(false);
  const [associationsLoaded, setAssociationsLoaded] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const searchRequestId = useRef(0);
  const saveRequestId = useRef(0);
  const savingRef = useRef(false);
  const associationRequestId = useRef(0);
  const loadedPropertyKey = useRef(draft?.loadedPropertyKey);
  const savedRouteIdsRef = useRef(savedRouteIds);
  const selectedRouteIdsRef = useRef(selectedRouteIds);
  const draggedRouteIndex = useRef();
  const featureFlagRefreshInFlight = useRef(false);

  const isDirty = !haveSameRouteOrder(selectedRouteIds, savedRouteIds);
  const canEditSelections =
    !viewOnlyMode && isPropertyReady && associationsLoaded && !loadingAssociations && !associationsError && !saving;
  const selectionAtLimit = selectedRouteIds.length >= maximumSelections;
  const remainingSelections = Math.max(maximumSelections - selectedRouteIds.length, 0);

  useEffect(() => {
    savedRouteIdsRef.current = savedRouteIds;
    selectedRouteIdsRef.current = selectedRouteIds;
  }, [savedRouteIds, selectedRouteIds]);

  useNearbyRoutesUnsavedWarning(isDirty);

  useEffect(() => {
    if (!draftRef) return;
    draftRef.current = {
      selectedRouteIds,
      savedRouteIds,
      version,
      routeDetails,
      hasConflict,
      conflictRecovered,
      loadedPropertyKey: loadedPropertyKey.current,
    };
  }, [draftRef, selectedRouteIds, savedRouteIds, version, routeDetails, hasConflict, conflictRecovered]);

  const mergeRouteDetails = useCallback(items => {
    setRouteDetails(currentDetails =>
      (items || []).reduce((details, route) => ({ ...details, [route.id]: route }), currentDetails),
    );
  }, []);

  const reconcileDisabledFeatureFlag = useCallback(
    error => {
      if (!isNearbyRouteFeatureDisabledError(error)) return false;
      if (!featureFlagRefreshInFlight.current) {
        featureFlagRefreshInFlight.current = true;
        Promise.resolve(dispatch(refreshFeatureFlagsAfterNearbyRouteDisabled(error))).finally(() => {
          featureFlagRefreshInFlight.current = false;
        });
      }
      return true;
    },
    [dispatch],
  );

  const loadAssociations = useCallback(
    async (recoverConflict = false) => {
      setLoadingAssociations(false);
      if (!isActive || !propertyId || savingRef.current) return;
      const requestId = associationRequestId.current + 1;
      associationRequestId.current = requestId;
      setLoadingAssociations(true);
      setAssociationsError();
      setPropertyNotFound(false);
      try {
        const response = await NearbyRouteService.getPropertyAssociations(propertyType, propertyId, languageCode);
        if (requestId !== associationRequestId.current) return;
        setAssociationsLoaded(true);
        setPropertyMissing(false);
        const orderedIds = (response.associations || [])
          .slice()
          .sort((left, right) => left.displayOrder - right.displayOrder)
          .map(association => association.nearbyRouteId);
        const propertyKey = `${propertyType}:${propertyId}`;
        let preserveDraftVersion = false;
        if (loadedPropertyKey.current !== propertyKey) {
          loadedPropertyKey.current = propertyKey;
          setSavedRouteIds(orderedIds);
          setSelectedRouteIds(orderedIds);
        } else {
          const selectionsAreDirty = !haveSameRouteOrder(selectedRouteIdsRef.current, savedRouteIdsRef.current);
          preserveDraftVersion = selectionsAreDirty;
          if (!selectionsAreDirty || recoverConflict === true) setSavedRouteIds(orderedIds);
          if (!selectionsAreDirty) setSelectedRouteIds(orderedIds);
        }
        if (!preserveDraftVersion || recoverConflict === true) setVersion(response.version);
        if (recoverConflict === true) {
          setHasConflict(false);
          setSaveError();
          setConflictRecovered(true);
        }
        setMaximumSelections(response.limits?.maxSelections || NEARBY_ROUTE_DEFAULTS.MAX_SELECTIONS);
        mergeRouteDetails(response.items);
      } catch (error) {
        if (requestId === associationRequestId.current) {
          if (reconcileDisabledFeatureFlag(error)) {
            return;
          }
          if (error?.response?.data?.code === "PROPERTY_NOT_FOUND" && !viewOnlyMode) {
            setAssociationsLoaded(false);
            setPropertyMissing(true);
            setPropertyNotFound(true);
          } else {
            setAssociationsError(getErrorMessage(error, t("NearbyRoutes.LoadError"), t("NearbyRoutes.CorrelationId")));
          }
        }
      } finally {
        if (requestId === associationRequestId.current) setLoadingAssociations(false);
      }
    },
    [
      isActive,
      languageCode,
      mergeRouteDetails,
      propertyId,
      propertyType,
      reconcileDisabledFeatureFlag,
      t,
      viewOnlyMode,
    ],
  );

  const loadResults = useCallback(async () => {
    setLoadingResults(false);
    if (!isActive || !propertyId) return;
    if (!isPropertyReady || !marketId) {
      setRoutes(currentRoutes => (currentRoutes.length ? [] : currentRoutes));
      setPagination(currentPagination => (currentPagination.totalItems ? EMPTY_PAGINATION : currentPagination));
      return;
    }
    const requestId = searchRequestId.current + 1;
    searchRequestId.current = requestId;
    setLoadingResults(true);
    setResultsError();
    try {
      const response = await NearbyRouteService.search({
        language: languageCode,
        market: marketId,
        page,
        pageSize,
        propertySubMarket: propertySubMarketId,
        search,
        sortDirection,
        sortField,
        subMarket,
        type: routeType,
      });
      if (requestId !== searchRequestId.current) return;
      setRoutes(response.items || []);
      setPagination(response.pagination || EMPTY_PAGINATION);
      setRouteTypes(response.filterOptions?.types || []);
      setAvailableSubMarketIds(response.filterOptions?.subMarkets || []);
      setMaximumSelections(response.limits?.maxSelections || NEARBY_ROUTE_DEFAULTS.MAX_SELECTIONS);
      mergeRouteDetails(response.items);
    } catch (error) {
      if (requestId === searchRequestId.current) {
        if (reconcileDisabledFeatureFlag(error)) return;
        setRoutes([]);
        setResultsError(getErrorMessage(error, t("NearbyRoutes.ResultsError"), t("NearbyRoutes.CorrelationId")));
      }
    } finally {
      if (requestId === searchRequestId.current) setLoadingResults(false);
    }
  }, [
    isActive,
    isPropertyReady,
    languageCode,
    marketId,
    mergeRouteDetails,
    page,
    pageSize,
    propertyId,
    propertySubMarketId,
    reconcileDisabledFeatureFlag,
    routeType,
    search,
    sortDirection,
    sortField,
    subMarket,
    t,
  ]);

  useEffect(() => {
    loadAssociations();
    return () => {
      associationRequestId.current += 1;
    };
  }, [loadAssociations]);

  useEffect(() => {
    loadResults();
    return () => {
      searchRequestId.current += 1;
    };
  }, [loadResults]);

  useEffect(
    () => () => {
      saveRequestId.current += 1;
    },
    [],
  );

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setPage(NEARBY_ROUTE_DEFAULTS.PAGE);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSubMarket(undefined);
    setRouteType(undefined);
    setPage(NEARBY_ROUTE_DEFAULTS.PAGE);
  };

  const selectRoute = (route, selected) => {
    if (!canEditSelections) return;
    if (!selected) {
      setSelectedRouteIds(currentIds => currentIds.filter(routeId => routeId !== route.id));
      return;
    }
    setSelectedRouteIds(currentIds => appendSelectedRoute(currentIds, route.id, maximumSelections));
    mergeRouteDetails([route]);
  };

  const selectVisibleRoutes = (selected, selectedRows, changedRows) => {
    if (!canEditSelections) return;
    const visibleRoutes = changedRows || selectedRows || [];
    setSelectedRouteIds(currentIds =>
      updateVisibleRouteSelections(
        currentIds,
        visibleRoutes.map(route => route.id),
        selected,
        maximumSelections,
      ),
    );
    if (selected) mergeRouteDetails(visibleRoutes);
  };

  const removeRoute = routeId => {
    if (!canEditSelections) return;
    setSelectedRouteIds(currentIds => currentIds.filter(selectedId => selectedId !== routeId));
    setAnnouncement(t("NearbyRoutes.RemovedAnnouncement"));
  };

  const removeAllRoutes = () => {
    if (!canEditSelections) return;
    setSelectedRouteIds([]);
    setAnnouncement(t("NearbyRoutes.RemovedAllAnnouncement"));
  };

  const reorderRoute = (sourceIndex, destinationIndex) => {
    if (!canEditSelections) return;
    setSelectedRouteIds(currentIds => moveSelectedRoute(currentIds, sourceIndex, destinationIndex));
    setAnnouncement(t("NearbyRoutes.ReorderedAnnouncement", { position: destinationIndex + 1 }));
  };

  const dropRoute = destinationIndex => {
    if (draggedRouteIndex.current === undefined) return;
    reorderRoute(draggedRouteIndex.current, destinationIndex);
    draggedRouteIndex.current = undefined;
  };

  const reorderRouteWithKeyboard = (event, sourceIndex) => {
    if (!canEditSelections || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const destinationIndex = sourceIndex + (event.key === "ArrowUp" ? -1 : 1);
    if (destinationIndex < 0 || destinationIndex >= selectedRouteIds.length) return;
    reorderRoute(sourceIndex, destinationIndex);
  };

  const handleTableChange = (nextPagination, filters, sorter) => {
    const nextPageSize = nextPagination.pageSize || pageSize;
    setPage(nextPageSize === pageSize ? nextPagination.current : NEARBY_ROUTE_DEFAULTS.PAGE);
    setPageSize(nextPageSize);
    if (sorter.field) {
      const nextSortDirection = sorter.order === "descend" ? "desc" : "asc";
      setSortField(sorter.field);
      setSortDirection(nextSortDirection);
      setAnnouncement(
        t("NearbyRoutes.SortAnnouncement", {
          column: t(`NearbyRoutes.${sorter.field.charAt(0).toUpperCase()}${sorter.field.slice(1)}`),
          direction: t(nextSortDirection === "asc" ? "NearbyRoutes.SortAscending" : "NearbyRoutes.SortDescending"),
        }),
      );
    }
  };

  const saveSelections = async () => {
    if (!canEditSelections || !isDirty || hasConflict || savingRef.current) return;
    savingRef.current = true;
    saveRequestId.current += 1;
    const requestId = saveRequestId.current;
    associationRequestId.current += 1;
    setSaving(true);
    setSaveError();
    try {
      const response = await NearbyRouteService.savePropertyAssociations(propertyType, propertyId, {
        associations: buildAssociations(selectedRouteIds),
        version,
      });
      if (requestId !== saveRequestId.current) return;
      const persistedIds = (response.associations || [])
        .slice()
        .sort((left, right) => left.displayOrder - right.displayOrder)
        .map(association => association.nearbyRouteId);
      setSavedRouteIds(persistedIds);
      setSelectedRouteIds(persistedIds);
      setVersion(response.version);
      setConflictRecovered(false);
      toastMessage("success", t("NearbyRoutes.SaveSuccess"));
    } catch (error) {
      if (requestId !== saveRequestId.current) return;
      if (reconcileDisabledFeatureFlag(error)) return;
      setHasConflict(error?.response?.status === 409);
      setSaveError(getErrorMessage(error, t("NearbyRoutes.SaveError"), t("NearbyRoutes.CorrelationId")));
    } finally {
      if (requestId === saveRequestId.current) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  const savePropertyAndDisplayRoutes = async () => {
    const requestId = saveRequestId.current;
    setSavingProperty(true);
    try {
      const saved = await onSaveAndDisplay();
      if (saved && requestId === saveRequestId.current) await loadAssociations();
    } finally {
      if (requestId === saveRequestId.current) setSavingProperty(false);
    }
  };

  const marketLabelsById = useMemo(() => buildOptionLabelLookup(markets), [markets]);
  const submarketLabelsById = useMemo(() => buildOptionLabelLookup(subMarkets), [subMarkets]);

  const columns = useMemo(() => {
    const sortableColumn = (field, width) => {
      const columnLabel = t(`NearbyRoutes.${field.charAt(0).toUpperCase()}${field.slice(1)}`);
      const isActiveSort = field === sortField;
      let ariaSort = "none";
      let directionKey = "NearbyRoutes.SortNone";
      if (isActiveSort) {
        ariaSort = sortDirection === "asc" ? "ascending" : "descending";
        directionKey = sortDirection === "asc" ? "NearbyRoutes.SortAscending" : "NearbyRoutes.SortDescending";
      }
      const currentDirection = t(directionKey);
      return {
        dataIndex: field,
        key: field,
        onHeaderCell: () => ({
          "aria-label": t("NearbyRoutes.SortColumnLabel", {
            column: columnLabel,
            direction: currentDirection,
          }),
          "aria-sort": ariaSort,
          onKeyDown: event => {
            if (!["Enter", " "].includes(event.key)) return;
            event.preventDefault();
            const nextSortDirection = isActiveSort && sortDirection === "asc" ? "desc" : "asc";
            setPage(NEARBY_ROUTE_DEFAULTS.PAGE);
            setSortField(field);
            setSortDirection(nextSortDirection);
            setAnnouncement(
              t("NearbyRoutes.SortAnnouncement", {
                column: columnLabel,
                direction: t(
                  nextSortDirection === "asc" ? "NearbyRoutes.SortAscending" : "NearbyRoutes.SortDescending",
                ),
              }),
            );
          },
          tabIndex: 0,
        }),
        render: value => value || EMPTY_VALUE,
        sorter: true,
        sortOrder: getColumnSortOrder(field, sortField, sortDirection),
        title: columnLabel,
        width,
      };
    };

    return [
      sortableColumn("name", 190),
      {
        dataIndex: "marketAssignments",
        key: "market",
        render: assignments => getMarketAssignmentLabels(assignments, marketLabelsById).join(", ") || EMPTY_VALUE,
        title: t("NearbyRoutes.Market"),
        width: 170,
      },
      {
        dataIndex: "marketAssignments",
        key: "submarkets",
        render: assignments => {
          const labels = getSubmarketAssignmentLabels(assignments, submarketLabelsById);
          if (!labels.length) return EMPTY_VALUE;
          const visibleLabels = labels.slice(0, MAX_VISIBLE_SUBMARKETS);
          const additionalLabels = labels.slice(MAX_VISIBLE_SUBMARKETS);
          return (
            <span className="nearby-routes__submarket-labels">
              {visibleLabels.map(label => (
                <Tag className="nearby-routes__submarket-label" key={label} title={label}>
                  {label}
                </Tag>
              ))}
              {additionalLabels.length > 0 && (
                <Tag
                  aria-label={`Additional submarkets: ${additionalLabels.join(", ")}`}
                  className="nearby-routes__submarket-label nearby-routes__submarket-overflow"
                  title={additionalLabels.join(", ")}
                >
                  {`+${additionalLabels.length}`}
                </Tag>
              )}
            </span>
          );
        },
        title: t("NearbyRoutes.Submarkets"),
        width: 210,
      },
      sortableColumn("type", 130),
      sortableColumn("address", 300),
    ];
  }, [marketLabelsById, sortDirection, sortField, submarketLabelsById, t]);

  const subMarketOptions = useMemo(
    () => availableSubMarketIds.map(value => ({ value, label: submarketLabelsById[value] || value })),
    [availableSubMarketIds, submarketLabelsById],
  );

  if (!isActive) {
    return <Prompt message={t("NearbyRoutes.UnsavedChanges")} when={isDirty} />;
  }

  return (
    <section className="nearby-routes" aria-labelledby="nearby-routes-heading" dir={getTextDirection(languageCode)}>
      <Prompt message={t("NearbyRoutes.UnsavedChanges")} when={isDirty} />
      <AppModal
        cancelText={t("Buttons.Cancel")}
        confirmLoading={savingProperty}
        onCancel={() => setPropertyNotFound(false)}
        onOk={savePropertyAndDisplayRoutes}
        okText={t("NearbyRoutes.SaveAndDisplay")}
        title={t("NearbyRoutes.PropertyNotFoundTitle")}
        visible={propertyNotFound && !viewOnlyMode}
      >
        <p>{t("NearbyRoutes.PropertyNotFoundHelperText")}</p>
      </AppModal>
      <div className="nearby-routes__heading">
        <h2 id="nearby-routes-heading">{t("NearbyRoutes.Title")}</h2>
        <Typography.Text aria-atomic="true" aria-live="polite" className="nearby-routes__selected-count" strong>
          {t("NearbyRoutes.SelectedCount", {
            count: selectedRouteIds.length,
            maximum: maximumSelections,
          })}
        </Typography.Text>
      </div>
      <Row className="nearby-routes__filters" gutter={[12, 12]}>
        <Col xs={24} md={10}>
          <Input.Search
            aria-label={t("NearbyRoutes.SearchLabel")}
            maxLength={NEARBY_ROUTE_DEFAULTS.MAX_SEARCH_LENGTH}
            onChange={event => setSearchInput(event.target.value)}
            placeholder={t("NearbyRoutes.SearchPlaceholder")}
            value={searchInput}
          />
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            allowClear
            aria-label={t("NearbyRoutes.Submarket")}
            onChange={value => {
              setSubMarket(value);
              setPage(NEARBY_ROUTE_DEFAULTS.PAGE);
            }}
            placeholder={t("NearbyRoutes.Submarket")}
            style={{ width: "100%" }}
            value={subMarket}
          >
            {subMarketOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            allowClear
            aria-label={t("NearbyRoutes.Type")}
            onChange={value => {
              setRouteType(value);
              setPage(NEARBY_ROUTE_DEFAULTS.PAGE);
            }}
            placeholder={t("NearbyRoutes.Type")}
            style={{ width: "100%" }}
            value={routeType}
          >
            {routeTypes.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={4}>
          <Button block onClick={clearFilters} disabled={!searchInput && !subMarket && !routeType}>
            {t("NearbyRoutes.ClearFilters")}
          </Button>
        </Col>
      </Row>

      {associationsError ? (
        <Alert
          className="nearby-routes__status"
          action={<Button onClick={loadAssociations}>{t("NearbyRoutes.Retry")}</Button>}
          message={associationsError}
          showIcon
          type="error"
        />
      ) : null}
      {propertyMissing && !propertyNotFound && !viewOnlyMode ? (
        <Alert
          className="nearby-routes__status"
          action={<Button onClick={() => setPropertyNotFound(true)}>{t("NearbyRoutes.SaveAndDisplay")}</Button>}
          message={t("NearbyRoutes.PropertyNotFoundTitle")}
          showIcon
          type="info"
        />
      ) : null}
      {selectionAtLimit ? (
        <Alert
          className="nearby-routes__status"
          message={t("NearbyRoutes.MaximumMessage", { maximum: maximumSelections })}
          showIcon
          type="info"
        />
      ) : null}
      {conflictRecovered ? (
        <Alert
          className="nearby-routes__status"
          message={t("NearbyRoutes.ConflictReview")}
          description={
            savedRouteIds.map(id => routeDetails[id]?.name || id).join(", ") || t("NearbyRoutes.NoneSelected")
          }
          showIcon
          type="info"
        />
      ) : null}
      {saveError || hasConflict ? (
        <Alert
          className="nearby-routes__status"
          action={
            <Button
              disabled={saving || loadingAssociations}
              onClick={hasConflict ? () => loadAssociations(true) : saveSelections}
            >
              {t(hasConflict ? "NearbyRoutes.ReloadLatest" : "NearbyRoutes.RetrySave")}
            </Button>
          }
          message={hasConflict ? t("NearbyRoutes.ConflictMessage") : saveError}
          showIcon
          type="error"
        />
      ) : null}
      <span aria-live="polite" className="nearby-routes__announcement">
        {announcement}
      </span>

      <Spin spinning={loadingAssociations}>
        <Row className="nearby-routes__content" gutter={16}>
          <Col xs={24} lg={15}>
            <div className="nearby-routes__results">
              {resultsError ? (
                <Alert
                  action={<Button onClick={loadResults}>{t("NearbyRoutes.Retry")}</Button>}
                  message={resultsError}
                  showIcon
                  type="error"
                />
              ) : (
                <Table
                  columns={columns}
                  dataSource={routes}
                  loading={loadingResults}
                  locale={{ emptyText: <Empty description={t("NearbyRoutes.NoResults")} /> }}
                  onChange={handleTableChange}
                  pagination={{
                    current: page,
                    pageSize,
                    pageSizeOptions: NEARBY_ROUTE_PAGE_SIZES.map(String),
                    showSizeChanger: true,
                    total: pagination.totalItems || 0,
                  }}
                  rowKey="id"
                  rowSelection={{
                    getCheckboxProps: route => ({
                      "aria-label": t("NearbyRoutes.SelectRoute", { name: route.name }),
                      disabled: !canEditSelections || (selectionAtLimit && !selectedRouteIds.includes(route.id)),
                    }),
                    onSelect: selectRoute,
                    onSelectAll: selectVisibleRoutes,
                    preserveSelectedRowKeys: true,
                    selectedRowKeys: selectedRouteIds,
                  }}
                  scroll={{ x: 1000 }}
                />
              )}
            </div>
          </Col>
          <Col xs={24} lg={9}>
            <div className="nearby-routes__selected">
              <div className="nearby-routes__selected-header">
                <h3>{t("NearbyRoutes.Selected")}</h3>
                {!viewOnlyMode && selectedRouteIds.length ? (
                  <Button
                    className="nearby-routes__remove-all"
                    disabled={!canEditSelections}
                    onClick={removeAllRoutes}
                    type="link"
                  >
                    {t("NearbyRoutes.RemoveAll")}
                  </Button>
                ) : null}
              </div>
              {!selectedRouteIds.length ? <Empty description={t("NearbyRoutes.NoneSelected")} /> : null}
              <ol className="nearby-routes__selected-list">
                {selectedRouteIds.map((routeId, index) => {
                  const route = routeDetails[routeId] || { id: routeId, name: routeId };
                  const RouteTypeIcon = ROUTE_TYPE_ICONS[route.filterType || route.type] || EnvironmentOutlined;
                  return (
                    <li
                      className="nearby-routes__selected-item"
                      draggable={canEditSelections}
                      key={routeId}
                      onDragEnd={() => {
                        draggedRouteIndex.current = undefined;
                      }}
                      onDragOver={event => event.preventDefault()}
                      onDragStart={() => {
                        draggedRouteIndex.current = index;
                      }}
                      onDrop={() => dropRoute(index)}
                    >
                      <span
                        aria-disabled={!canEditSelections}
                        aria-keyshortcuts="ArrowUp ArrowDown"
                        aria-label={t("NearbyRoutes.DragRoute", { name: route.name })}
                        aria-valuemax={selectedRouteIds.length}
                        aria-valuemin={1}
                        aria-valuenow={index + 1}
                        className="nearby-routes__drag-handle"
                        onKeyDown={event => reorderRouteWithKeyboard(event, index)}
                        role="spinbutton"
                        tabIndex={canEditSelections ? 0 : -1}
                      >
                        &#8942;&#8942;
                      </span>
                      <span
                        aria-label={route.type || t("NearbyRoutes.Type")}
                        className="nearby-routes__route-type-icon"
                        role="img"
                      >
                        <RouteTypeIcon />
                      </span>
                      <span className="nearby-routes__route-text">
                        <strong className="nearby-routes__route-name" title={route.name || route.id}>
                          {route.name || route.id}
                        </strong>
                        <span className="nearby-routes__route-meta">{route.address || EMPTY_VALUE}</span>
                      </span>
                      {!viewOnlyMode ? (
                        <span className="nearby-routes__order-actions">
                          <Button
                            aria-label={t("NearbyRoutes.RemoveRoute", { name: route.name })}
                            danger
                            disabled={!canEditSelections}
                            icon={<DeleteOutlined />}
                            onClick={() => removeRoute(routeId)}
                            size="small"
                          />
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
              {selectedRouteIds.length && remainingSelections ? (
                <div className="nearby-routes__selection-guidance">
                  <span className="nearby-routes__selection-guidance-title">
                    <PlusCircleOutlined aria-hidden="true" className="nearby-routes__selection-guidance-icon" />
                    {t("NearbyRoutes.SelectMore", { count: remainingSelections })}
                  </span>
                  <span>{t("NearbyRoutes.MaximumGuidance", { maximum: maximumSelections })}</span>
                </div>
              ) : null}
            </div>
          </Col>
        </Row>
      </Spin>

      {!viewOnlyMode ? (
        <div className="nearby-routes__actions">
          <Popconfirm
            cancelText={t("Buttons.No")}
            disabled={!isDirty}
            okText={t("Buttons.Yes")}
            onConfirm={() => {
              setSelectedRouteIds(savedRouteIds);
              setSaveError();
              setConflictRecovered(false);
            }}
            title={t("NearbyRoutes.CancelConfirmation")}
          >
            <Button disabled={!isDirty || saving}>{t("Buttons.Cancel")}</Button>
          </Popconfirm>
          <Button
            disabled={!canEditSelections || !isDirty || hasConflict}
            loading={saving}
            onClick={saveSelections}
            type="primary"
          >
            {t("NearbyRoutes.Save")}
          </Button>
        </div>
      ) : null}
    </section>
  );
};

NearbyRoutes.propTypes = {
  draftRef: PropTypes.shape({ current: PropTypes.object }),
  isActive: PropTypes.bool,
  isPropertyReady: PropTypes.bool,
  languageCode: PropTypes.string,
  marketId: PropTypes.string,
  markets: PropTypes.arrayOf(PropTypes.object),
  onSaveAndDisplay: PropTypes.func,
  propertyId: PropTypes.string.isRequired,
  propertySubMarketId: PropTypes.string,
  propertyType: PropTypes.oneOf(["building", "land"]).isRequired,
  subMarkets: PropTypes.arrayOf(PropTypes.object),
  viewOnlyMode: PropTypes.bool,
};

NearbyRoutes.defaultProps = {
  draftRef: undefined,
  isActive: false,
  isPropertyReady: false,
  languageCode: "en",
  marketId: undefined,
  markets: [],
  onSaveAndDisplay: () => Promise.resolve(false),
  propertySubMarketId: undefined,
  subMarkets: [],
  viewOnlyMode: false,
};

export default NearbyRoutes;
